import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { cleanString, dateWithAsterisk, formattedDate } from "../../../utils";
import { LoadedSince } from "../../../shared-components/LoadedSince";
import Pagination from "../../../shared-components/Pagination";

import { genericErrorLoadingMessage, reportFailed } from "../../utils";

import { usePermissionValidator } from "../../../api/UserPermissionValidators";
import { useAppContext } from "../../../AppProviders";
import { CacheKeys } from "../../../api/Cache";
import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ClipboardIcon,
} from "@heroicons/react/24/outline";
import Table from "../../Table";
import MisconductCancelButton from "../../MisconductCancelButton";
import ReportStatus from "../../ReportStatus";
import {
  ReportEntry,
  ReportEntryStyle,
  ReportRowItem,
} from "../../helper-components/TableModels";
import { ReportSummaryItemApi } from "../../../api/Reports";
import { useMisconductReportSummaryListQuery } from "../misconductListQueries";
import ReactTooltip from "react-tooltip";

const pageSize = 15;

function convertClientQaMisconductSummaryRows(
  summaries: ReportSummaryItemApi[] | undefined,
  canCancelMisconduct: boolean
): ReportRowItem<ReportSummaryItemApi>[] {
  if (!summaries) return [];
  return summaries.map((report) => {
    const data: ReportEntry[] = [
      {
        text: report.full_name,
        style: ReportEntryStyle.COMPONENT,
        component: (
          <div className="flex">
            <div>
              <span className="font-semibold">{report.full_name}</span>
              <small className="block font-normal">{report.company_name}</small>
            </div>
            <div>
              {(report.step_payload?.rerun_from_parent_uuid ||
                report.has_rerun) && (
                <div>
                  <ArrowPathIcon className="w-5 ml-2 text-blue-600" />
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        text: "",
        style: ReportEntryStyle.COMPONENT,
        component: (
          <ReportStatus
            stepStatusName={report.step_name}
            failed={reportFailed(report)}
            report={report}
            allowResumeOnClick={true}
          />
        ),
      },
      {
        component: (
          <div className="flex flex-col">
            <span className="border-b-2">{report.blueprint_name}</span>{" "}
            <small className="block">
              {report.behavior_kit_name ?? "General"}
            </small>
          </div>
        ),
        text:
          report.blueprint_name + " " + (report.behavior_kit_name ?? "General"),
        style: ReportEntryStyle.COMPONENT,
      },
      {
        text: formattedDate(report.report_date_added),
        style: ReportEntryStyle.COMPONENT,
        component: report.recur ? (
          <>
            <span>{formattedDate(report.report_date_added)}</span>
            <span data-tip="Employee Screening" className="cursor-pointer">
              <ArrowPathRoundedSquareIcon className="w-5 text-gray-500" />
            </span>
            <ReactTooltip effect="solid" />
          </>
        ) : (
          <div>{formattedDate(report.report_date_added)}</div>
        ),
        customClass: "pl-8 flex-1",
      },
      {
        text: formattedDate(report.due_date),
        style: ReportEntryStyle.COMPONENT,
        component: dateWithAsterisk(
          new Date(report.due_date),
          report.due_date_updated,
          "long"
        ),
        customClass: "w-48",
      },
      {
        text: report.report_uuid,
        style: ReportEntryStyle.COMPONENT,
        component: (
          <div className="flex gap-1">
            <ClipboardIcon
              onClick={() => {
                navigator.clipboard.writeText(report.report_uuid);
              }}
              className="w-4 cursor-pointer"
            />
            <span className="w-16 h-[30px]">
              {report.report_uuid.slice(0, 7)}
            </span>
          </div>
        ),
      },
      {
        text: `${report.client_report_id}`,
        style: ReportEntryStyle.TEXT,
      },
    ];
    if (canCancelMisconduct) {
      data.push({
        text: "Cancel",
        style: ReportEntryStyle.COMPONENT,
        component: <MisconductCancelButton reportUUID={report.report_uuid} />,
      });
    }
    return {
      report,
      report_uuid: report.report_uuid,
      isLocked: report.is_locked,
      data,
    };
  });
}

/**
 * Client QA tab — ``GET /lists/misconduct/client_qa``. Legacy: ``../../ClientQATab.tsx``.
 */
export function ClientQATabV2026() {
  const { canViewAllReportsWithinCompany, canCancelMisconduct } =
    usePermissionValidator();
  const { refreshCache } = useAppContext();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [filter, setFilter] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const v2026CqaQuery = useMisconductReportSummaryListQuery({
    tab: "client_qa",
    individual: !canViewAllReportsWithinCompany,
    names: filter,
    page,
    pageSize,
    enabled: true,
  });
  const SearchName = searchParams.get("search");
  const SearchPage = searchParams.get("page");

  const tableHeader = [
    "Name / Company",
    "Status",
    "Check Type / Behavior Kit",
    "Date Added",
    "Date Due",
    "Report ID",
    "Client Reference ID",
  ];
  if (canCancelMisconduct) {
    tableHeader.push("Cancel Report");
  }

  useEffect(() => {
    setFilter(cleanString(SearchName?.split(" ").join("%20") ?? ""));
    setPage(Number(1));
  }, [SearchName]);

  useEffect(() => {
    setPage(Number(SearchPage || "1"));
  }, [SearchPage]);

  if (v2026CqaQuery.error) {
    return (
      <div className="p-4 rounded-xl bg-red-300 text-red-900">
        {genericErrorLoadingMessage}
      </div>
    );
  }

  const v2026Data = v2026CqaQuery.data;
  return (
    <>
      <LoadedSince
        reportData={v2026Data}
        onClear={() => {
          refreshCache(CacheKeys.reports);
          void v2026CqaQuery.refetch();
        }}
      />
      <Table
        isLoading={v2026CqaQuery.isLoading}
        header={tableHeader}
        data={convertClientQaMisconductSummaryRows(
          v2026Data?.report_summaries,
          canCancelMisconduct
        )}
      />
      {!v2026CqaQuery.isLoading && v2026Data && (
        <Pagination
          pageSize={pageSize}
          currentPage={page}
          totalCount={v2026Data.total_rows}
          search={filter}
        />
      )}
    </>
  );
}
